import { L } from '@thingslog/ui-components';

export interface ClusterableDevice {
  deviceNumber: string;
  latitude: number;
  longitude: number;
}

export interface DeviceCluster {
  deviceNumbers: string[];
  latitude: number;
  longitude: number;
}

export const CLUSTER_PIXEL_RADIUS = 48;

/**
 * Greedy pixel-space clustering: devices whose projected positions at the given
 * zoom level are within `pixelRadius` of a cluster's anchor are merged into it.
 */
export const clusterDevices = (
  devices: ClusterableDevice[],
  zoom: number,
  pixelRadius: number = CLUSTER_PIXEL_RADIUS
): DeviceCluster[] => {
  const clusters: { members: ClusterableDevice[]; anchor: L.Point }[] = [];

  devices.forEach((device: ClusterableDevice) => {
    const point = L.CRS.EPSG3857.latLngToPoint(L.latLng(device.latitude, device.longitude), zoom);
    const existing = clusters.find(
      (cluster: { members: ClusterableDevice[]; anchor: L.Point }) =>
        cluster.anchor.distanceTo(point) <= pixelRadius
    );
    if (existing) {
      existing.members.push(device);
    } else {
      clusters.push({ members: [device], anchor: point });
    }
  });

  return clusters.map((cluster: { members: ClusterableDevice[]; anchor: L.Point }) => ({
    deviceNumbers: cluster.members.map((member: ClusterableDevice) => member.deviceNumber),
    latitude:
      cluster.members.reduce((sum: number, member: ClusterableDevice) => sum + member.latitude, 0) /
      cluster.members.length,
    longitude:
      cluster.members.reduce(
        (sum: number, member: ClusterableDevice) => sum + member.longitude,
        0
      ) / cluster.members.length,
  }));
};
